import React, { useState, useEffect } from 'react';
import { 
  sendBulkEmails, 
  checkEmailConfig, 
  generateNotificationEmailTemplate 
} from '../utils/nodemailerService';
import { toast } from 'react-toastify';

const BulkEmailSender = () => {
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [results, setResults] = useState(null);

  // Check if email is configured on component mount
  useEffect(() => {
    const verifyEmailConfig = async () => {
      try {
        const config = await checkEmailConfig();
        setIsConfigured(config.isConfigured);
      } catch (error) {
        console.error('Error checking email config:', error);
        setIsConfigured(false);
      }
    };

    verifyEmailConfig();
  }, []);

  const handleSendBulkEmails = async (e) => {
    e.preventDefault();
    
    if (!recipients || !subject || !message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      // Parse the recipients (comma-separated emails or JSON format)
      let recipientsList = [];
      
      try {
        // First try to parse as JSON
        recipientsList = JSON.parse(recipients);
      } catch {
        // If not JSON, split by commas or newlines
        const lines = recipients.split(/[\n,]/).filter(Boolean);
        
        recipientsList = lines.map(line => {
          const email = line.trim();
          return { email };
        });
      }
      
      if (!Array.isArray(recipientsList) || recipientsList.length === 0) {
        throw new Error('Invalid recipients format');
      }
      
      // Create the email template
      const template = (recipient) => {
        return generateNotificationEmailTemplate({
          firstName: recipient.firstName || 'Student',
          subject,
          message
        });
      };
      
      const result = await sendBulkEmails(recipientsList, subject, template);
      
      if (result.success) {
        toast.success(`Successfully sent ${result.results.filter(r => r.success).length} of ${recipientsList.length} emails`);
        setResults(result);
      } else {
        toast.error(`Failed to send emails: ${result.message}`);
        setResults(result);
      }
    } catch (error) {
      console.error('Error sending bulk emails:', error);
      toast.error(`Error sending emails: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm p-4 mb-4">
      <h4 className="mb-4">Bulk Email Sender</h4>

      {!isConfigured && (
        <div className="alert alert-warning">
          <p className="mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Email service is not configured. Please configure it in the Email Settings first.
          </p>
        </div>
      )}

      <form onSubmit={handleSendBulkEmails}>
        <div className="mb-3">
          <label htmlFor="recipients" className="form-label">Recipients</label>
          <textarea
            id="recipients"
            className="form-control"
            rows="4"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            placeholder="Enter email addresses (one per line, or comma-separated)"
            required
            disabled={!isConfigured || loading}
          ></textarea>
          <div className="form-text">
            Enter one email per line, comma-separated emails, or a JSON array in the format: 
            [&#123;"email": "user1@example.com", "firstName": "John"&#125;, &#123;"email": "user2@example.com", "firstName": "Jane"&#125;]
          </div>
        </div>
        
        <div className="mb-3">
          <label htmlFor="subject" className="form-label">Subject</label>
          <input
            type="text"
            id="subject"
            className="form-control"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            disabled={!isConfigured || loading}
          />
        </div>
        
        <div className="mb-3">
          <label htmlFor="bulkMessage" className="form-label">Message</label>
          <textarea
            id="bulkMessage"
            className="form-control"
            rows="5"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            disabled={!isConfigured || loading}
          ></textarea>
          <div className="form-text">You can use HTML formatting in your message.</div>
        </div>
        
        <button 
          type="submit" 
          className="btn btn-primary" 
          disabled={!isConfigured || loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Sending Emails...
            </>
          ) : 'Send Bulk Emails'}
        </button>
      </form>

      {results && (
        <div className="mt-4">
          <h5>Results</h5>
          <div className="alert alert-info">
            <p>Sent {results.results.filter(r => r.success).length} of {results.results.length} emails.</p>
          </div>
          
          {results.errors && results.errors.length > 0 && (
            <div className="card border-danger mt-3">
              <div className="card-header bg-danger text-white">
                <h6 className="mb-0">Errors ({results.errors.length})</h6>
              </div>
              <div className="card-body">
                <ul className="list-group">
                  {results.errors.map((error, index) => (
                    <li key={index} className="list-group-item text-danger">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="card mt-3">
            <div className="card-header">
              <h6 className="mb-0">Detailed Results</h6>
            </div>
            <div className="card-body p-0">
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.results.map((result, index) => (
                      <tr key={index} className={result.success ? 'table-success' : 'table-danger'}>
                        <td>{result.email}</td>
                        <td>
                          {result.success ? (
                            <span className="text-success">
                              <i className="bi bi-check-circle me-1"></i>
                              Success
                            </span>
                          ) : (
                            <span className="text-danger">
                              <i className="bi bi-x-circle me-1"></i>
                              Failed: {result.error}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkEmailSender; 